import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Nav from 'react-bootstrap/Nav';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';

const IMAPConfigurationModal = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('gmail');

  const PRIMARY_COLOR = '#224B6A';
  const SECONDARY_COLOR = '#90CBD8';

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const configurations = {
    gmail: {
      title: 'Configurar IMAP en Gmail',
      description: 'Sigue estos pasos para configurar IMAP en tu cuenta de Gmail',
      icon: '📧',
      steps: [
        {
          number: 1,
          titulo: 'Habilitar IMAP en Gmail',
          detalles: [
            'Accede a tu cuenta de Gmail',
            'Haz clic en el ícono de engranaje (⚙️) en la esquina superior derecha',
            'Selecciona "Ver todos los ajustes"',
            'Ve a la pestaña "Reenvío e IMAP"',
            'En la sección IMAP, marca la opción "Habilitar IMAP"',
            'Haz clic en "Guardar cambios"'
          ]
        },
        {
          number: 2,
          titulo: 'Crear contraseña de aplicación',
          detalles: [
            'Ve a tu Cuenta de Google (https://myaccount.google.com)',
            'En el panel izquierdo, selecciona "Seguridad"',
            'Desplázate hasta "Contraseñas de aplicaciones" (requiere 2FA habilitado)',
            'Selecciona la aplicación de correo y dispositivo',
            'Google te proporcionará una contraseña de 16 caracteres',
            'Copia esta contraseña (la necesitarás en tu cliente de correo)'
          ]
        }
      ],
      config: {
        servidor: 'imap.gmail.com',
        puerto: 993,
        seguridad: 'SSL/TLS',
        usuario: 'tu_email@gmail.com',
        contraseña: 'Contraseña de aplicación (16 caracteres)'
      },
      notas: [
        { tipo: 'warning', texto: 'Es obligatorio usar contraseña de aplicación, NO tu contraseña de Gmail regular' },
        { tipo: 'success', texto: 'Gmail soporta IMAP completamente' },
        { tipo: 'success', texto: 'Los cambios pueden tardar hasta 24 horas en activarse' }
      ]
    },
    outlook: {
      title: 'Configurar IMAP en Outlook',
      description: 'Guía para configurar IMAP en Outlook (cuenta personal o buzón compartido)',
      icon: '📬',
      tabs: [
        {
          name: 'Cuenta Personal',
          steps: [
            {
              number: 1,
              titulo: 'Acceso a Configuración de Seguridad',
              detalles: [
                'Accede a tu cuenta de Outlook en https://account.microsoft.com',
                'Selecciona "Seguridad" en el menú lateral',
                'Busca la opción "Aplicaciones y contraseñas"'
              ]
            },
            {
              number: 2,
              titulo: 'Generar Contraseña de Aplicación',
              detalles: [
                'Haz clic en "Crear una nueva contraseña de aplicación"',
                'Selecciona Correo como aplicación',
                'Selecciona tu dispositivo',
                'Microsoft generará una contraseña de 16 caracteres',
                'Guarda esta contraseña en un lugar seguro'
              ]
            }
          ],
          config: {
            servidor: 'imap-mail.outlook.com',
            puerto: 993,
            seguridad: 'SSL/TLS',
            usuario: 'tu_email@outlook.com',
            contraseña: 'Contraseña de aplicación'
          }
        },
        {
          name: 'Buzón Compartido',
          steps: [
            {
              number: 1,
              titulo: 'Verificar Permisos de Acceso',
              detalles: [
                'Asegúrate de tener permisos "Editor" o superior en el buzón compartido',
                'El propietario del buzón debe otorgarte acceso desde Outlook Web',
                'Ve a Configuración > Buzones compartidos y verifica tu acceso'
              ]
            },
            {
              number: 2,
              titulo: 'Configurar IMAP para Buzón Compartido',
              detalles: [
                'Usa el mismo servidor: imap-mail.outlook.com',
                'Puerto: 993 con SSL/TLS',
                'Usuario: el email del PROPIETARIO del buzón',
                'Contraseña: contraseña de aplicación del propietario',
                'En algunas aplicaciones, usa el formato: propietario@dominio.com/buzón_compartido'
              ]
            },
            {
              number: 3,
              titulo: 'Alternativa: Agregar desde Outlook',
              detalles: [
                'Si prefieres método alternativo, abre Outlook Desktop',
                'Ve a Archivo > Agregar cuenta',
                'Ingresa los datos del buzón compartido',
                'Sigue los pasos de autenticación'
              ]
            }
          ],
          config: {
            servidor: 'imap-mail.outlook.com',
            puerto: 993,
            seguridad: 'SSL/TLS',
            usuario: 'email_propietario@outlook.com',
            contraseña: 'Contraseña de aplicación del propietario',
            nota: 'Formato alternativo: propietario@dominio.com/buzón_compartido'
          }
        }
      ],
      notas: [
        { tipo: 'warning', texto: 'Requiere autenticación de dos factores (2FA) habilitada' },
        { tipo: 'success', texto: 'Outlook soporta IMAP completamente' },
        { tipo: 'success', texto: 'Para buzones compartidos, necesitas permisos explícitos del propietario' }
      ]
    },
    otros: {
      title: 'Configurar IMAP en Otros Servidores',
      description: 'Guía general para configurar IMAP en servidores de correo personalizados',
      icon: '🌐',
      steps: [
        {
          number: 1,
          titulo: 'Obtener Datos del Servidor',
          detalles: [
            'Contacta al administrador de tu servidor de correo',
            'Solicita los siguientes datos:',
            '  • Servidor IMAP (ej: mail.tudominio.com)',
            '  • Puerto IMAP (usualmente 993 para SSL/TLS o 143 para STARTTLS)',
            '  • Tipo de encriptación (SSL/TLS o STARTTLS)',
            '  • Tu nombre de usuario (puede ser tu email o un usuario específico)',
            '  • Si requiere autenticación especial'
          ]
        },
        {
          number: 2,
          titulo: 'Verificar Disponibilidad de IMAP',
          detalles: [
            'Confirma que IMAP esté habilitado en el servidor',
            'Algunos servidores solo soportan POP3',
            'Pregunta por la política de seguridad (contraseñas de aplicación, 2FA, etc.)',
            'Solicita información sobre límites de conexiones simultáneas'
          ]
        },
        {
          number: 3,
          titulo: 'Configurar en tu Cliente',
          detalles: [
            'Abre tu cliente de correo (Thunderbird, Evolution, Mail, etc.)',
            'Selecciona "Agregar cuenta" o "Nueva cuenta"',
            'Ingresa tu email y contraseña',
            'Si la detección automática no funciona, ingresa manualmente:',
            '  • Servidor IMAP proporcionado',
            '  • Puerto: generalmente 993 (SSL/TLS) o 143 (STARTTLS)',
            '  • Cifrado: SSL/TLS o STARTTLS según el servidor',
            '  • Usuario y contraseña proporcionada'
          ]
        },
        {
          number: 4,
          titulo: 'Prueba de Conexión',
          detalles: [
            'Una vez configurado, intenta enviar un correo de prueba',
            'Verifica que puedas recibir y enviar mensajes',
            'Si tienes errores de autenticación:',
            '  • Verifica que usuario y contraseña sean correctos',
            '  • Revisa si el servidor requiere cambios en usuario (puede incluir dominio)',
            '  • Comprueba que el puerto sea el correcto'
          ]
        }
      ],
      commonServers: [
        { nombre: 'ProtonMail', imap: 'imap.protonmail.com', puerto: 993, seguridad: 'SSL/TLS' },
        { nombre: 'Zoho Mail', imap: 'imap.zoho.com', puerto: 993, seguridad: 'SSL/TLS' },
        { nombre: 'Yandex Mail', imap: 'imap.yandex.com', puerto: 993, seguridad: 'SSL/TLS' },
        { nombre: 'Mail.ru', imap: 'imap.mail.ru', puerto: 993, seguridad: 'SSL/TLS' },
        { nombre: 'Postfix (personalizado)', imap: 'mail.tudominio.com', puerto: 993, seguridad: 'SSL/TLS' },
      ],
      notas: [
        { tipo: 'warning', texto: 'Cada servidor puede tener configuración única' },
        { tipo: 'success', texto: 'Siempre verifica la documentación oficial del proveedor' },
        { tipo: 'success', texto: 'Si hay errores, revisa los logs del servidor para más información' }
      ]
    }
  };

  const renderConfig = (config) => (
    <Card className="mb-3" style={{ border: `2px solid ${SECONDARY_COLOR}`, borderRadius: '8px' }}>
      <Card.Body style={{ backgroundColor: `${SECONDARY_COLOR}15` }}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <small style={{ color: PRIMARY_COLOR, fontWeight: 'bold' }}>Servidor IMAP</small>
            <p className="font-monospace mb-0" style={{ fontSize: '13px', color: PRIMARY_COLOR }}>
              {config.servidor}
            </p>
          </div>
          <div className="col-md-6 mb-3">
            <small style={{ color: PRIMARY_COLOR, fontWeight: 'bold' }}>Puerto</small>
            <p className="font-monospace mb-0" style={{ fontSize: '13px', color: PRIMARY_COLOR }}>
              {config.puerto}
            </p>
          </div>
          <div className="col-md-6 mb-3">
            <small style={{ color: PRIMARY_COLOR, fontWeight: 'bold' }}>Seguridad</small>
            <p className="font-monospace mb-0" style={{ fontSize: '13px', color: PRIMARY_COLOR }}>
              {config.seguridad}
            </p>
          </div>
          <div className="col-md-6 mb-3">
            <small style={{ color: PRIMARY_COLOR, fontWeight: 'bold' }}>Usuario</small>
            <p className="font-monospace mb-0" style={{ fontSize: '13px', color: PRIMARY_COLOR }}>
              {config.usuario}
            </p>
          </div>
          {config.nota && (
            <div className="col-12">
              <small style={{ color: PRIMARY_COLOR, fontWeight: 'bold' }}>Nota</small>
              <p className="font-monospace mb-0" style={{ fontSize: '13px', color: PRIMARY_COLOR }}>
                {config.nota}
              </p>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  const renderContent = () => {
    const content = configurations[activeTab];

    if (activeTab === 'gmail') {
      return (
        <div>
          <h5 style={{ color: PRIMARY_COLOR, marginBottom: '8px', fontWeight: 'bold' }}>
            {content.icon} {content.title}
          </h5>
          <p className="text-muted" style={{ fontSize: '14px', marginBottom: '20px' }}>
            {content.description}
          </p>

          {content.steps.map((step) => (
            <Card key={step.number} className="mb-3" style={{ border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <Card.Body style={{ padding: '16px' }}>
                <div className="d-flex align-items-start mb-3">
                  <span
                    className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                    style={{
                      width: '36px',
                      height: '36px',
                      backgroundColor: PRIMARY_COLOR,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '16px'
                    }}
                  >
                    {step.number}
                  </span>
                  <h6 style={{ color: PRIMARY_COLOR, marginBottom: '0', fontWeight: 'bold' }}>
                    {step.titulo}
                  </h6>
                </div>
                <ul style={{ paddingLeft: '50px', marginBottom: '0' }}>
                  {step.detalles.map((detalle, idx) => (
                    <li key={idx} style={{ marginBottom: '8px', color: '#333', fontSize: '14px' }}>
                      {detalle}
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Card>
          ))}

          {renderConfig(content.config)}

          {content.notas.map((nota, idx) => (
            <Alert key={idx} variant={nota.tipo === 'warning' ? 'warning' : 'success'} className="mb-2" style={{ fontSize: '13px' }}>
              {nota.texto}
            </Alert>
          ))}
        </div>
      );
    }

    if (activeTab === 'outlook') {
      return (
        <div>
          <h5 style={{ color: PRIMARY_COLOR, marginBottom: '8px', fontWeight: 'bold' }}>
            {content.icon} {content.title}
          </h5>
          <p className="text-muted" style={{ fontSize: '14px', marginBottom: '20px' }}>
            {content.description}
          </p>

          {content.tabs.map((tab, tabIdx) => (
            <div key={tabIdx} className="mb-4">
              <div
                style={{
                  padding: '10px 14px',
                  backgroundColor: SECONDARY_COLOR,
                  color: 'white',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  marginBottom: '14px',
                  fontSize: '14px'
                }}
              >
                {tab.name}
              </div>

              {tab.steps.map((step) => (
                <Card key={step.number} className="mb-3" style={{ border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <Card.Body style={{ padding: '16px' }}>
                    <div className="d-flex align-items-start mb-3">
                      <span
                        className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                        style={{
                          width: '32px',
                          height: '32px',
                          backgroundColor: SECONDARY_COLOR,
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}
                      >
                        {step.number}
                      </span>
                      <h6 style={{ color: PRIMARY_COLOR, marginBottom: '0', fontWeight: 'bold' }}>
                        {step.titulo}
                      </h6>
                    </div>
                    <ul style={{ paddingLeft: '50px', marginBottom: '0' }}>
                      {step.detalles.map((detalle, idx) => (
                        <li key={idx} style={{ marginBottom: '8px', color: '#333', fontSize: '14px' }}>
                          {detalle}
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              ))}

              {renderConfig(tab.config)}
            </div>
          ))}

          {content.notas.map((nota, idx) => (
            <Alert key={idx} variant={nota.tipo === 'warning' ? 'warning' : 'success'} className="mb-2" style={{ fontSize: '13px' }}>
              {nota.texto}
            </Alert>
          ))}
        </div>
      );
    }

    if (activeTab === 'otros') {
      return (
        <div>
          <h5 style={{ color: PRIMARY_COLOR, marginBottom: '8px', fontWeight: 'bold' }}>
            {content.icon} {content.title}
          </h5>
          <p className="text-muted" style={{ fontSize: '14px', marginBottom: '20px' }}>
            {content.description}
          </p>

          {content.steps.map((step) => (
            <Card key={step.number} className="mb-3" style={{ border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <Card.Body style={{ padding: '16px' }}>
                <div className="d-flex align-items-start mb-3">
                  <span
                    className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                    style={{
                      width: '36px',
                      height: '36px',
                      backgroundColor: PRIMARY_COLOR,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '16px'
                    }}
                  >
                    {step.number}
                  </span>
                  <h6 style={{ color: PRIMARY_COLOR, marginBottom: '0', fontWeight: 'bold' }}>
                    {step.titulo}
                  </h6>
                </div>
                <ul style={{ paddingLeft: '50px', marginBottom: '0' }}>
                  {step.detalles.map((detalle, idx) => (
                    <li key={idx} style={{ marginBottom: '8px', color: '#333', fontSize: '14px' }}>
                      {detalle}
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Card>
          ))}

          <Card className="mb-3" style={{ border: `2px solid ${SECONDARY_COLOR}`, borderRadius: '8px' }}>
            <Card.Body style={{ backgroundColor: `${SECONDARY_COLOR}15` }}>
              <h6 style={{ color: PRIMARY_COLOR, marginBottom: '14px', fontWeight: 'bold' }}>
                Servidores Comunes
              </h6>
              <Table striped hover size="sm" style={{ marginBottom: '0' }}>
                <thead style={{ backgroundColor: SECONDARY_COLOR, color: 'white' }}>
                  <tr>
                    <th>Proveedor</th>
                    <th>IMAP</th>
                    <th>Puerto</th>
                    <th>Seguridad</th>
                  </tr>
                </thead>
                <tbody>
                  {content.commonServers.map((server, idx) => (
                    <tr key={idx} style={{ fontSize: '13px' }}>
                      <td>{server.nombre}</td>
                      <td className="font-monospace">{server.imap}</td>
                      <td>{server.puerto}</td>
                      <td>{server.seguridad}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {content.notas.map((nota, idx) => (
            <Alert key={idx} variant={nota.tipo === 'warning' ? 'warning' : 'success'} className="mb-2" style={{ fontSize: '13px' }}>
              {nota.texto}
            </Alert>
          ))}
        </div>
      );
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header style={{ backgroundColor: PRIMARY_COLOR, borderBottom: `3px solid ${SECONDARY_COLOR}` }} closeButton>
        <Modal.Title style={{ color: 'white', fontWeight: 'bold' }}>
          📧 Configuración IMAP
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '24px' }}>
        <Nav variant="tabs" className="mb-4" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav.Item>
            <Nav.Link
              eventKey="gmail"
              style={{
                color: activeTab === 'gmail' ? PRIMARY_COLOR : '#666',
                borderBottom: activeTab === 'gmail' ? `3px solid ${PRIMARY_COLOR}` : '1px solid #ddd',
                fontWeight: activeTab === 'gmail' ? 'bold' : 'normal'
              }}
            >
              📧 Gmail
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="outlook"
              style={{
                color: activeTab === 'outlook' ? PRIMARY_COLOR : '#666',
                borderBottom: activeTab === 'outlook' ? `3px solid ${PRIMARY_COLOR}` : '1px solid #ddd',
                fontWeight: activeTab === 'outlook' ? 'bold' : 'normal'
              }}
            >
              📬 Outlook
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="otros"
              style={{
                color: activeTab === 'otros' ? PRIMARY_COLOR : '#666',
                borderBottom: activeTab === 'otros' ? `3px solid ${PRIMARY_COLOR}` : '1px solid #ddd',
                fontWeight: activeTab === 'otros' ? 'bold' : 'normal'
              }}
            >
              🌐 Otros Servidores
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {renderContent()}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: `1px solid ${SECONDARY_COLOR}` }}>
        <Button style={{ backgroundColor: PRIMARY_COLOR, borderColor: PRIMARY_COLOR }} onClick={onClose}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IMAPConfigurationModal;
